import { useQuery } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import type { AppTitleLogReviewResponse } from '@/types/reports';

export interface UseAppTitleLogReviewArgs {
  /** Set to false to skip the fetch (e.g. when review=1 is absent from URL). */
  enabled: boolean;
  periodQs: string | null;
  app: string;
  title: string;
  clientId?: string;
  projectId?: string;
  employeeId?: string;
}

interface UseAppTitleLogReviewResult {
  data: AppTitleLogReviewResponse | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Fires the AI vision review endpoint for one Activity Log row.
 * The call is slow (several seconds — it runs a Claude vision call server-side).
 * Only fires when `enabled` is true and the period is resolved.
 */
export function useAppTitleLogReview(args: UseAppTitleLogReviewArgs): UseAppTitleLogReviewResult {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const shouldFetch =
    !mockupMode &&
    args.enabled &&
    !!orgId &&
    !!args.periodQs &&
    args.app !== undefined &&
    args.title !== undefined;

  const query = useQuery({
    queryKey: ['appTitleLogReview', orgId, args],
    queryFn: async () => {
      const token = await getToken();
      const params = new URLSearchParams(args.periodQs ?? '');
      params.set('app', args.app);
      params.set('title', args.title);
      if (args.clientId) params.set('clientId', args.clientId);
      if (args.projectId) params.set('projectId', args.projectId);
      if (args.employeeId) params.set('employeeId', args.employeeId);
      return apiFetch<AppTitleLogReviewResponse>(
        `/api/v1/orgs/${orgId}/reports/activity-log/review?${params.toString()}`,
        token,
      );
    },
    enabled: shouldFetch,
    // Long stale time — result won't change between navigations in the same session.
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  if (mockupMode || !args.enabled) return { data: null, isLoading: false, error: null };

  const errorMsg = query.error
    ? query.error instanceof Error
      ? query.error.message
      : 'Unknown error'
    : null;

  return { data: query.data ?? null, isLoading: query.isLoading, error: errorMsg };
}
